/**
 * BOTTOM SHEET ACTION COMPONENT
 *
 * Reusable action button for bottom sheet menus (mobile overflow menus).
 * Provides consistent styling across all admin bottom sheets.
 *
 * Variants:
 * - neutral: White overlay with subtle brightness increase on hover (default for most actions)
 * - text: Transparent background with text color and subtle hover fill (for secondary actions like Cancel)
 * - danger: Red text with subtle red tint on hover (for destructive actions)
 * - primary: Teal background with dark text (for main CTAs)
 *
 * Focus behavior:
 * - Uses :focus-visible for keyboard-only focus rings
 * - Prevents "stuck hover" appearance on mobile tap
 */

interface BottomSheetActionProps {
  label: string;
  onClick: () => void;
  variant?: 'neutral' | 'text' | 'danger' | 'primary';
  disabled?: boolean;
  loading?: boolean;
}

export default function BottomSheetAction({
  label,
  onClick,
  variant = 'neutral',
  disabled = false,
  loading = false,
}: BottomSheetActionProps) {
  const baseClasses =
    'w-full px-4 py-3 rounded-lg text-[14px] font-medium text-left transition-colors';

  const variantClasses = {
    neutral:
      'bg-white/[0.14] text-zinc-100 hover:bg-white/20 active:bg-white/25 disabled:bg-white/[0.14] disabled:text-zinc-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#40E0D0] focus-visible:outline-offset-2',
    text:
      'text-zinc-400 hover:bg-white/10 active:bg-white/[0.15] disabled:text-zinc-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#40E0D0] focus-visible:outline-offset-2',
    danger:
      'text-red-400 hover:bg-red-500/10 active:bg-red-500/15 disabled:text-red-500/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#40E0D0] focus-visible:outline-offset-2',
    primary:
      'bg-[#40E0D0] text-[#0A0A0A] hover:bg-[#47EAD8] active:bg-[#3DD4C4] disabled:bg-[#40E0D0]/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white focus-visible:outline-offset-2',
  };

  const disabledClasses = 'disabled:cursor-not-allowed';

  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className={`${baseClasses} ${variantClasses[variant]} ${disabledClasses}`}
    >
      {loading ? `${label}...` : label}
    </button>
  );
}
